import type { Entries, PartialDeep } from 'type-fest';

// biome-ignore lint/suspicious/noExplicitAny: intended
export type $TSFixMe = any;

export type $TSFixMeFunction = (...args: $TSFixMe[]) => $TSFixMe;

export type RequiredKeys<T, K extends keyof T> = Exclude<T, K> &
  Required<Pick<T, K>>;

export type XOR<T1, T2> =
  | (T1 & { [k in Exclude<keyof T2, keyof T1>]?: never })
  | (T2 & { [k in Exclude<keyof T1, keyof T2>]?: never });

type Falsey = null | undefined | false | '' | 0 | 0n;
type Truthy<T> = T extends Falsey ? never : T;

export type Nullable<T> = T | undefined | null;

export type DeepPartial<T> = PartialDeep<T, { recurseIntoArrays: true }>;

export function assertDefined<T>(
  value: T,
  name: string = 'Value',
): asserts value {
  if (value === null) {
    throw new TypeError(`${name} should not be null.`);
  }
  if (value === undefined) {
    throw new TypeError(`${name} should not be undefined.`);
  }
}

export function typedEntries<T extends object>(obj: T): Entries<T> {
  return Object.entries(obj) as Entries<T>;
}

export function typedFromEntries<T extends Record<string, unknown>>(
  entries: Entries<T>,
): T {
  return Object.fromEntries(entries) as T;
}

export function typedKeys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

export function defined<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export function propertiesDefined<T extends Record<string, unknown>>(
  obj: T,
): obj is { [K in keyof T]: NonNullable<T[K]> } {
  return Object.values(obj).every(defined);
}

export function truthy<T>(value: T): value is Truthy<T> {
  return !!value;
}

// Used to override static methods for a class
// https://github.com/microsoft/TypeScript/issues/4628
export type OmitAllStatics<
  T extends { new (...args: $TSFixMe[]): $TSFixMe; prototype: $TSFixMe },
> = T extends { new (...args: infer A): infer R; prototype: infer P }
  ? { new (...args: A): R; prototype: P }
  : never;
